import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const navItems = [
  {
    title: 'CFSC',
    path: '/home#top',
    dropdown: []
  },
  {
    title: 'About Us',
    path: '/about',
    dropdown: [
      { label: 'The Centre', path: '/about#top' },
      { label: 'Our Team', path: '/about/team#top' },
      { label: 'Partners', path: '/about/partners#top' },
      { label: 'Careers', path: '/about/careers#top' },
      { label: 'FAQs', path: '/about/faqs#top' }
    ]
  },
  {
    title: 'Capabilities',
    path: '/capabilities',
    dropdown: [
      { label: 'Climate Finance and Economics', path: '/capabilities/climate-finance#top' },
      { label: 'Energy Finance', path: '/capabilities/energy-finance' },
      { label: 'Nature and Biodiversity Finance', path: '/capabilities/biodiversity-finance#top' }
    ]
  },
  {
    title: 'Programs',
    path: '/programs',
    dropdown: [
      { label: 'Climate Adaptation Finance Professional Certificate', path: '/programs/cafp#top' }
    ]
  },
  /*{
    title: 'Insights | Research',
    path: '/insights',
    dropdown: [
      { label: 'Insights', path: '/insights#top' },
      { label: 'Research', path: '/research#top' }
    ]
  },*/
  {
    title: 'News | Events',
    path: '/news',
    dropdown: [
      { label: 'News', path: '/news#top' },
      { label: 'Events', path: '/events#top' }
    ]
  },
  {
    title: 'Contact',
    path: '/contact#top',
    dropdown: []
  }
];

export default function Navbar() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.nav-item') && !target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Reset state on route change
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const toggleDropdown = (title: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveDropdown(activeDropdown === title ? null : title);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed top-0 w-full bg-white shadow-md z-50 ${isScrolled ? 'bg-opacity-95' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/home" className="text-2xl font-bold text-green-700">CFSC</Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <div key={item.title} className="nav-item relative">
                <button
                  onClick={(e) => item.dropdown.length ? toggleDropdown(item.title, e) : handleNavigation(item.path)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${isActive(item.path) ? 'text-green-600' : 'text-gray-700 hover:text-green-600'}`}
                >
                  <span className="flex items-center">
                    {item.title}
                    {item.dropdown.length > 0 && (
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${
                        activeDropdown === item.title ? 'rotate-180' : ''
                      }`} />
                    )}
                  </span>
                </button>

                {item.dropdown.length > 0 && activeDropdown === item.title && (
                  <div className="nav-dropdown absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {item.dropdown.map((subItem) => (
                        <button
                          key={subItem.label}
                          onClick={() => handleNavigation(subItem.path)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600"
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-green-600 focus:outline-none"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-16 inset-x-0 bg-white shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <div key={item.title} className="nav-item">
                <button
                  onClick={(e) => item.dropdown.length ? toggleDropdown(item.title, e) : handleNavigation(item.path)}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-green-600 hover:bg-green-50"
                >
                  <span className="flex items-center justify-between">
                    {item.title}
                    {item.dropdown.length > 0 && (
                      <ChevronDown className={`h-4 w-4 transition-transform ${
                        activeDropdown === item.title ? 'rotate-180' : ''
                      }`} />
                    )}
                  </span>
                </button>
                {item.dropdown.length > 0 && activeDropdown === item.title && (
                  <div className="pl-4 space-y-1">
                    {item.dropdown.map((subItem) => (
                      <button
                        key={subItem.label}
                        onClick={() => handleNavigation(subItem.path)}
                        className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-green-600 hover:bg-green-50"
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}